if (!customElements.get('lens-selector')) {
  customElements.define('lens-selector', class extends HTMLElement {
    connectedCallback() {
      this.optionNames = JSON.parse(this.querySelector('[data-option-names]').textContent);
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent).map(variant => ({
        ...variant,
        selected: Object.fromEntries(this.optionNames.map((name, index) => [name.toLowerCase(), variant.options[index]])),
      }));
      this.state = { step: 1, usage: '', coating: '', index: '', rxMethod: 'now', rx: {}, variant: null, profileId: '', saveProfile: false, profileName: '' };
      this.profiles = [];
      this.profilesLoaded = false;
      this.profilesLoading = false;
      this.profilesAvailable = true;
      this.profileError = '';
      this.signedIn = this.dataset.customerSignedIn === 'true';
      this.funnelSessionId = '';
      this.funnelStartedAt = 0;
      this.funnelCompleted = false;
      this.funnelAbandoned = false;
      this.lastTrackedStep = 0;
      this.closeReason = '';
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('[data-content]');
      this.next = this.querySelector('[data-next]');
      this.back = this.querySelector('[data-back]');
      this.error = this.querySelector('[data-error]');
      this.total = this.querySelector('[data-total]');
      this.labels = { 'non-prescription': 'Non-prescription blue light blocking', 'single-vision': 'Single vision', reading: 'Reading' };
      this.section = this.closest('.product__info-container');
      this.nativeForm = this.section?.querySelector('product-form form[data-type="add-to-cart-form"]');
      this.nativeProductForm = this.nativeForm?.closest('product-form');
      if (!this.nativeForm || !this.availableVariants().some(variant => this.isPrescription(variant))) return;
      this.hideNativeControls();
      this.querySelector('[data-open]').addEventListener('click', () => {
        this.syncColor();
        this.startFunnel('product_page');
        this.render();
        this.dialog.showModal();
        if (this.signedIn && !this.profilesLoaded && !this.profilesLoading) this.loadProfiles();
      });
      this.querySelector('[data-close]').addEventListener('click', () => {
        this.closeReason = 'close_button';
        this.dialog.close();
      });
      this.next.addEventListener('click', () => this.advance());
      this.back.addEventListener('click', () => this.previous());
      this.dialog.addEventListener('change', event => this.onChange(event));
      this.dialog.addEventListener('click', event => {
        if (event.target.closest('[data-profile-login]')) this.saveResumeState();
      });
      this.dialog.addEventListener('cancel', () => { this.closeReason = 'escape_key'; });
      this.dialog.addEventListener('close', () => this.trackAbandon(this.closeReason || 'dialog_closed'));
      window.addEventListener('pagehide', () => {
        if (this.dialog.open) this.trackAbandon('page_exit');
      });
      this.resumeAfterSignIn();
    }

    hideNativeControls() {
      this.section?.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input').forEach(group => {
        const name = group.querySelector('legend,label')?.textContent.trim().replace(':','').toLowerCase();
        if (name === 'lens' || name === 'lense' || name === 'lens coating') group.classList.add('ls-native-hidden');
      });
      this.nativeProductForm?.classList.add('ls-native-hidden');
    }

    render() {
      this.error.textContent = '';
      this.back.hidden = this.state.step === 1;
      this.querySelector('[data-progress-label]').textContent = this.state.step === 5 ? 'Final step' : `Step ${this.state.step} of 5`;
      this.querySelectorAll('.ls-progress span').forEach((item, index) => {
        item.classList.toggle('is-active', index + 1 === this.state.step);
        item.classList.toggle('is-done', index + 1 < this.state.step);
      });
      if (this.state.step === 1) this.renderUsage();
      if (this.state.step === 2) this.renderRx();
      if (this.state.step === 3) this.renderCoating();
      if (this.state.step === 4) this.renderIndex();
      if (this.state.step === 5) this.renderReview();
      const heading = this.content.querySelector('h2');
      if (heading) heading.id = `ls-heading-${this.dataset.sectionId}`;
      this.content.scrollTop = 0;
      this.updateFooter();
      this.trackStepView();
    }

    startFunnel(source) {
      this.funnelSessionId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      this.funnelStartedAt = Date.now();
      this.funnelCompleted = false;
      this.funnelAbandoned = false;
      this.lastTrackedStep = 0;
      this.closeReason = '';
      this.publishTracking('lens_funnel_open', { source });
    }

    stepName(step = this.state.step) {
      return ({ 1: 'usage', 2: 'prescription_details', 3: 'lens_type', 4: 'lens_index', 5: 'review' })[step] || 'unknown';
    }

    trackingContext() {
      return {
        funnel_session_id: this.funnelSessionId,
        product_id: this.dataset.productId || '',
        product_handle: this.dataset.productHandle || '',
        product_title: this.dataset.productTitle || '',
        variant_id: this.state.variant?.id || '',
        step_index: this.state.step,
        step_name: this.stepName(),
        usage: this.state.usage || 'not_selected',
        lens_type: this.state.coating || 'not_selected',
        lens_index: this.state.index || 'not_selected',
        prescription_path: this.state.usage === 'non-prescription' ? 'non_prescription' : (this.isRxDeferred() ? 'email_later' : (this.state.profileId ? 'saved_profile' : 'manual_entry')),
        elapsed_ms: this.funnelStartedAt ? Date.now() - this.funnelStartedAt : 0,
      };
    }

    publishTracking(name, details = {}) {
      if (!this.funnelSessionId) return;
      const payload = { ...this.trackingContext(), ...details };
      try { window.Shopify?.analytics?.publish?.(name, payload); } catch (error) { /* Tracking must never block ordering. */ }
      try { window.dataLayer?.push?.({ event: name, ...payload }); } catch (error) { /* Optional tag-manager fallback. */ }
    }

    trackStepView() {
      if (!this.funnelSessionId || this.lastTrackedStep === this.state.step) return;
      this.lastTrackedStep = this.state.step;
      this.publishTracking('lens_step_view');
    }

    trackStepComplete() {
      this.publishTracking('lens_step_complete');
    }

    trackAbandon(reason) {
      if (!this.funnelSessionId || this.funnelCompleted || this.funnelAbandoned) return;
      this.funnelAbandoned = true;
      this.publishTracking('lens_funnel_abandon', { abandon_reason: reason });
    }

    choice({ name, value, title, description, icon, price = '', badge = '', tone = '' }) {
      return `<label class="ls-option${badge ? ' ls-option--badged' : ''}"><input type="radio" name="${name}" value="${value}" ${this.state[name] === value ? 'checked' : ''}><span class="ls-option-icon${tone ? ` ls-option-icon--${tone}` : ''}">${icon}</span><span class="ls-option-copy"><strong>${title}</strong>${description ? `<small>${description}</small>` : ''}</span>${price ? `<span class="ls-option-price">${price}</span>` : ''}${badge ? `<span class="ls-badge">${badge}</span>` : ''}</label>`;
    }

    usageIcon(type) {
      if (type === 'screen') return '<svg viewBox="0 0 32 32" aria-hidden="true" focusable="false"><rect x="3.5" y="5" width="19" height="14.5" rx="2.2"/><path d="M9 25h8M13 19.5V25"/><path d="M25.5 11.5l3 1.2v3.1c0 2.2-1.2 4.2-3 5.3-1.8-1.1-3-3.1-3-5.3v-3.1l3-1.2z"/><path d="m24.4 16 1 1 1.8-2"/></svg>';
      if (type === 'vision') return '<svg viewBox="0 0 32 32" aria-hidden="true" focusable="false"><path d="M3.5 16s4.8-7 12.5-7 12.5 7 12.5 7-4.8 7-12.5 7S3.5 16 3.5 16z"/><circle cx="16" cy="16" r="4.2"/><circle cx="16" cy="16" r="1.2" class="ls-icon-fill"/></svg>';
      return '<svg viewBox="0 0 32 32" aria-hidden="true" focusable="false"><path d="M4.5 7.5h7.3c2.3 0 4.2 1.9 4.2 4.2v13.8c0-2.1-1.7-3.8-3.8-3.8H4.5V7.5z"/><path d="M27.5 7.5h-7.3c-2.3 0-4.2 1.9-4.2 4.2v13.8c0-2.1 1.7-3.8 3.8-3.8h7.7V7.5z"/><path d="M8 12h4.5M19.5 12H24M8 16h4.5M19.5 16H24"/></svg>';
    }

    coatingIcon(type) {
      if (type === 'blue') return '<svg viewBox="0 0 32 32" aria-hidden="true" focusable="false"><rect x="3.5" y="5" width="19" height="14.5" rx="2.2"/><path d="M9 25h8M13 19.5V25"/><path d="M25.5 11.5l3 1.2v3.1c0 2.2-1.2 4.2-3 5.3-1.8-1.1-3-3.1-3-5.3v-3.1l3-1.2z"/><path d="m24.4 16 1 1 1.8-2"/></svg>';
      return '<svg viewBox="0 0 32 32" aria-hidden="true" focusable="false"><circle cx="15.5" cy="16.5" r="9.5"/><path d="M12 9.2c-2.8 1.3-4.6 4.1-4.6 7.3"/><path d="M25.3 4.2v4.2M23.2 6.3h4.2M26.5 21.5v5M24 24h5"/><circle cx="23" cy="11.5" r="1.2" class="ls-icon-fill"/></svg>';
    }

    indexIcon(index) {
      const shapes = {
        '1.61': '<path d="M16 4.5C9.8 8.7 9.8 23.3 16 27.5M16 4.5c6.2 4.2 6.2 18.8 0 23"/><path d="M8 16h16M10.2 13.8 8 16l2.2 2.2M21.8 13.8 24 16l-2.2 2.2"/>',
        '1.67': '<path d="M16 4.5c-4.4 4.2-4.4 18.8 0 23M16 4.5c4.4 4.2 4.4 18.8 0 23"/><path d="M10.5 16h11M12.7 13.8 10.5 16l2.2 2.2M19.3 13.8l2.2 2.2-2.2 2.2"/>',
        '1.74': '<path d="M16 4.5c-2.8 4.2-2.8 18.8 0 23M16 4.5c2.8 4.2 2.8 18.8 0 23"/><path d="M12.5 16h7M14.2 14.3 12.5 16l1.7 1.7M17.8 14.3l1.7 1.7-1.7 1.7"/><path d="M24.5 5v4M22.5 7h4"/>',
      };
      return `<svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">${shapes[index]}</svg>`;
    }

    renderUsage() {
      const nonPrescription = this.candidates({ usage: 'non-prescription' });
      const prescription = this.candidates({ usage: 'single-vision' });
      const choices = [];
      if (nonPrescription.length) choices.push(this.choice({name:'usage',value:'non-prescription',title:'Non-prescription blue light blocking',description:'<span class="ls-selling-points">Natural-color lenses · 25% blue-light filtering · UV protection</span><span class="ls-selling-use">Made for everyday screen use</span>',icon:this.usageIcon('screen'),tone:'screen',price:'Included'}));
      if (prescription.length) {
        const from = `From ${this.money(this.minPrice(prescription))}`;
        choices.push(this.choice({name:'usage',value:'single-vision',title:'Single vision',description:'One prescription for distance or everyday wear',icon:this.usageIcon('vision'),tone:'vision',price:from}));
        choices.push(this.choice({name:'usage',value:'reading',title:'Reading',description:'One prescription optimized for near vision',icon:this.usageIcon('reading'),tone:'reading',price:from}));
      }
      this.content.innerHTML = `<h2 id="ls-heading-${this.dataset.sectionId}">Choose your lens usage</h2><p class="ls-support">Start with how you’ll use your ${this.escape(this.dataset.productTitle)} glasses.</p>${this.includedCoatingMessage()}<div class="ls-options">${choices.join('')}</div>`;
    }

    isRxDeferred() { return this.state.usage !== 'non-prescription' && this.state.rxMethod === 'later'; }

    prescriptionLaterMessage() {
      return '<p class="ls-note">Email your prescription and order number to <a href="mailto:contact@lesevenoptical.com">contact@lesevenoptical.com</a>. Or reply to your order confirmation email.</p>';
    }

    renderRx() {
      const sph = this.numberOptions(-12, 6, .25, 'Choose');
      const cyl = this.numberOptions(-6, 6, .25, 'Choose');
      const axisOptions = Array.from({length:180},(_,i)=>`<option value="${i+1}">${i+1}</option>`);
      axisOptions.splice(90, 0, '<option value="" selected>Choose CYL first</option>');
      const axis = axisOptions.join('');
      const pdOptions = Array.from({length:21},(_,i)=>`<option value="${i+55}">${i+55} mm</option>`);
      pdOptions.splice(10, 0, '<option value="" selected>Choose</option>');
      const pd = pdOptions.join('');
      this.content.innerHTML = `<h2>Your prescription</h2><p class="ls-support">Enter your details below, or send your prescription later.</p><fieldset class="ls-rx-fields" ${this.isRxDeferred() ? 'disabled' : ''}><legend class="visually-hidden">Prescription details</legend>${this.signedIn ? this.profilePanel() : `<p class="ls-rx-login"><a data-profile-login href="${this.escape(this.dataset.loginUrl)}">Sign in to use a saved prescription</a></p>`}<div class="ls-rx-head"><span>Eye</span><span>SPH</span><span>CYL</span><span>AXIS</span></div>${this.rxRow('od','OD','Right',sph,cyl,axis)}${this.rxRow('os','OS','Left',sph,cyl,axis)}<label class="ls-pd">Pupillary distance (PD)<select name="pd">${pd}</select><small>Enter the single PD number shown on your prescription.</small></label>${this.signedIn && this.profilesAvailable ? `<div class="ls-profile-save"><label><input type="checkbox" name="profile-save" ${this.state.saveProfile ? 'checked' : ''}> Save this prescription to my account</label><label ${this.state.saveProfile ? '' : 'hidden'} data-profile-name>Profile name (optional)<input type="text" name="profile-name" maxlength="60" value="${this.escape(this.state.profileName)}" placeholder="For example, Everyday glasses"></label><a href="${this.escape(this.dataset.accountProfilesUrl)}">Manage saved prescriptions</a></div>` : ''}</fieldset><div class="ls-rx-later"><p class="ls-rx-prompt" id="ls-rx-prompt-${this.dataset.sectionId}" data-rx-prompt role="alert" hidden></p><label><input type="checkbox" name="rx-later" aria-describedby="ls-rx-prompt-${this.dataset.sectionId}" ${this.isRxDeferred() ? 'checked' : ''}><span>Send prescription later</span></label>${this.prescriptionLaterMessage()}</div>`;
      this.restoreRx();
    }

    profilePanel() {
      if (!this.signedIn) return `<aside class="ls-profile-panel"><strong>Use this prescription again next time</strong><p>Sign in or create an account to load and save prescription profiles.</p><a class="button button--secondary" data-profile-login href="${this.escape(this.dataset.loginUrl)}">Sign in or create account</a><small>You can continue below as a guest without saving.</small></aside>`;
      if (this.profilesLoading) return '<aside class="ls-profile-panel"><strong>Saved prescriptions</strong><p>Loading your profiles…</p></aside>';
      if (!this.profilesAvailable) return `<aside class="ls-profile-panel"><strong>Saved prescriptions are temporarily unavailable</strong><p>${this.escape(this.profileError || 'Enter your prescription below to continue without saving it.')}</p></aside>`;
      if (!this.profiles.length) return '<aside class="ls-profile-panel"><strong>Saved prescriptions</strong><p>No saved prescriptions yet. Enter one below and choose to save it.</p></aside>';
      const options = this.profiles.map((profile) => `<option value="${this.escape(profile.id)}" ${this.state.profileId === profile.id ? 'selected' : ''}>${this.escape(profile.name || 'Unnamed prescription')}</option>`).join('');
      return `<aside class="ls-profile-panel"><label><strong>Use a saved prescription</strong><select name="profile-id"><option value="">Enter a new prescription</option>${options}</select></label><small>Selecting a profile fills the fields below. Review every value before continuing.</small></aside>`;
    }

    async profileFetch(options = {}) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 6000);
      try {
        return await fetch(this.dataset.profilesEndpoint, {...options, signal: controller.signal});
      } catch (error) {
        if (error.name === 'AbortError') throw new Error('Saved prescriptions took too long to respond. Enter your prescription below to continue without saving it.');
        throw error;
      } finally {
        clearTimeout(timeout);
      }
    }

    async loadProfiles() {
      this.profilesLoading = true;
      if (this.state.step === 2) this.render();
      try {
        const response = await this.profileFetch({headers: {'Accept': 'application/json'}, cache: 'no-store'});
        if (!String(response.headers.get('content-type') || '').includes('application/json')) throw new Error('Enter your prescription below to continue without saving it.');
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || 'Saved prescriptions could not be loaded.');
        this.profiles = Array.isArray(payload.profiles) ? payload.profiles : [];
        this.profilesLoaded = true;
      } catch (error) {
        this.profilesAvailable = false;
        this.profileError = error.message || 'Enter your prescription below to continue without saving it.';
      } finally {
        this.profilesLoading = false;
        if (this.state.step === 2) this.render();
      }
    }

    applyProfile(profile) {
      this.state.profileId = profile.id;
      this.state.profileName = profile.name || '';
      this.state.rx = {
        'od-sph': profile.od.sph, 'od-cyl': profile.od.cyl || '', 'od-axis': profile.od.axis || '',
        'os-sph': profile.os.sph, 'os-cyl': profile.os.cyl || '', 'os-axis': profile.os.axis || '',
        pd: profile.pd,
      };
    }

    async saveCurrentProfile() {
      this.state.profileName = this.content.querySelector('[name="profile-name"]')?.value.trim() || '';
      const payload = {
        name: this.state.profileName,
        odSph: this.state.rx['od-sph'], odCyl: this.state.rx['od-cyl'] || '', odAxis: this.state.rx['od-axis'] || '',
        osSph: this.state.rx['os-sph'], osCyl: this.state.rx['os-cyl'] || '', osAxis: this.state.rx['os-axis'] || '',
        pd: this.state.rx.pd,
      };
      const response = await this.profileFetch({
        method: 'POST',
        headers: {'Accept': 'application/json', 'Content-Type': 'application/json'},
        body: JSON.stringify(payload),
      });
      if (!String(response.headers.get('content-type') || '').includes('application/json')) throw new Error('Saving is temporarily unavailable. Continue without saving this prescription.');
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'The prescription could not be saved.');
      this.profiles = result.profiles;
      this.profilesLoaded = true;
      this.state.profileId = result.profile.id;
      this.state.profileName = result.profile.name || '';
      this.state.saveProfile = false;
    }

    saveResumeState() {
      try {
        sessionStorage.setItem('le-seven-lens-selector-resume', JSON.stringify({
          product: location.pathname,
          state: this.state,
        }));
      } catch (error) {
        // Sign-in still returns to the product even when session storage is unavailable.
      }
    }

    resumeAfterSignIn() {
      const url = new URL(location.href);
      if (url.searchParams.get('ls_resume') !== '1') return;
      url.searchParams.delete('ls_resume');
      history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      let saved;
      try {
        saved = JSON.parse(sessionStorage.getItem('le-seven-lens-selector-resume') || 'null');
        sessionStorage.removeItem('le-seven-lens-selector-resume');
      } catch (error) {
        saved = null;
      }
      if (saved?.product === location.pathname && saved.state) {
        this.state = {...this.state, ...saved.state, step: 2, variant: null};
      } else {
        this.state.usage = 'single-vision';
        this.state.step = 2;
      }
      this.syncColor();
      this.startFunnel('account_resume');
      this.render();
      this.dialog.showModal();
      if (this.signedIn && !this.profilesLoaded && !this.profilesLoading) this.loadProfiles();
    }

    rxRow(key, short, side, sph, cyl, axis) {
      return `<div class="ls-rx-row"><strong>${short}<small>${side}</small></strong><label>SPH<select name="${key}-sph" aria-label="${side} eye sphere">${sph}</select></label><label>CYL<select name="${key}-cyl" aria-label="${side} eye cylinder">${cyl}</select></label><label>AXIS<select name="${key}-axis" aria-label="${side} eye axis" disabled>${axis}</select></label></div>`;
    }

    renderCoating() {
      const clear = this.candidates({ usage: this.state.usage, coating: 'clear' });
      const blue = this.candidates({ usage: this.state.usage, coating: 'blue' });
      const baseline = this.coatingBaseline();
      const choices = [];
      if (clear.length) choices.push(this.choice({name:'coating',value:'clear',title:'Clear lenses',description:'<span class="ls-selling-points">Maximum clarity · UV protection</span><span class="ls-selling-use">A clean, classic lens for everyday wear</span>',icon:this.coatingIcon('clear'),tone:'clear',price:this.incrementLabel(this.minPrice(clear), baseline)}));
      if (blue.length) choices.push(this.choice({name:'coating',value:'blue',title:'Le Seven Signature<br>Blue Light Coating',description:'<span class="ls-selling-points">Natural-color lenses · 25% blue-light filtering · UV protection</span><span class="ls-selling-use">Made for everyday screen use</span>',icon:this.coatingIcon('blue'),tone:'coating-blue',price:this.incrementLabel(this.minPrice(blue), baseline),badge:'Recommended'}));
      this.content.innerHTML = `<h2>Choose your lens type</h2><p class="ls-support">Options shown are available for the selected frame color.</p><div class="ls-options">${choices.join('')}</div>`;
    }

    renderIndex() {
      const indexes = ['1.61','1.67','1.74'].filter(index => this.candidates({usage:this.state.usage, coating:this.state.coating, index}).length);
      const baseline = this.indexBaseline();
      this.content.innerHTML = `<h2>Choose your lens index</h2><p class="ls-support">Higher indexes create a thinner lens. Only available options are shown.</p><div class="ls-options">${indexes.map(index => {
        const variants = this.candidates({usage:this.state.usage, coating:this.state.coating, index});
        const meta = index === '1.61'
          ? ['Comfort','<span class="ls-index-standard">Balanced thickness · Everyday value</span><span class="ls-selling-use">A dependable choice for everyday prescriptions</span>','']
          : index === '1.67'
            ? ['Most Popular','<span class="ls-selling-points">Thinner profile · Lightweight comfort</span><span class="ls-selling-use">Our most popular choice</span>','Most popular']
            : ['Premium','<span class="ls-selling-points">Our thinnest lens · Refined appearance</span><span class="ls-selling-use">Designed for higher prescriptions</span>','High Prescription'];
        return this.choice({name:'index',value:index,title:`${index} · ${meta[0]}`,description:meta[1],icon:this.indexIcon(index),tone:`index-${index.replace('.','')}`,price:this.incrementLabel(this.minPrice(variants), baseline),badge:meta[2]});
      }).join('')}</div>`;
    }

    renderReview() {
      this.state.variant = this.resolveVariant();
      const rx = this.state.usage === 'non-prescription' ? '' : this.isRxDeferred() ? `<div class="ls-review-rx"><strong>Prescription: To be sent after checkout</strong>${this.prescriptionLaterMessage()}</div>` : `<div class="ls-review-rx"><strong>Prescription details</strong><br>OD: SPH ${this.state.rx['od-sph']} · CYL ${this.state.rx['od-cyl']||'—'} · AXIS ${this.state.rx['od-axis']||'—'}<br>OS: SPH ${this.state.rx['os-sph']} · CYL ${this.state.rx['os-cyl']||'—'} · AXIS ${this.state.rx['os-axis']||'—'}<br>PD: ${this.state.rx.pd} mm</div>`;
      this.content.innerHTML = `<h2>Review your order</h2><p class="ls-support">Check every detail before adding ${this.escape(this.dataset.productTitle)} to your cart.</p>${this.includedCoatingMessage()}<div class="ls-review"><div class="ls-review-product"><img src="${this.dataset.productImage}" alt=""><div><strong>${this.escape(this.dataset.productTitle)}</strong><small>${this.escape(this.selectedColor())}</small></div><strong>${this.state.variant?this.money(this.state.variant.price):'Unavailable'}</strong></div><dl><div><dt>Usage</dt><dd>${this.labels[this.state.usage]}</dd></div><div><dt>Lens type</dt><dd>${this.state.coating==='blue'?'Le Seven Signature Blue Light Coating':'Clear lenses'}</dd></div><div><dt>Lens index</dt><dd>${this.state.index||'Standard non-prescription'}</dd></div></dl>${rx}<div class="ls-review-total"><span>Subtotal</span><strong>${this.state.variant?this.money(this.state.variant.price):'Unavailable'}</strong></div></div>${this.state.usage==='non-prescription'?'':'<p class="ls-note">Custom-prescription products are not eligible for fit-and-style returns, refunds, or exchanges. Confirmed manufacturing defects, shipping damage, quality issues, and production prescription inaccuracies remain covered.</p>'}`;
    }

    onChange(event) {
      if (event.target.name === 'rx-later') {
        this.state.rxMethod = event.target.checked ? 'later' : 'now';
        if (this.isRxDeferred()) this.state.saveProfile = false;
        this.render();
        this.content.querySelector('[name="rx-later"]')?.focus();
        return;
      }
      if (event.target.name === 'usage') {
        this.state.usage = event.target.value;
        this.state.coating = event.target.value === 'non-prescription' ? 'blue' : '';
        this.state.index = '';
      }
      if (event.target.name === 'coating') { this.state.coating = event.target.value; this.state.index=''; }
      if (event.target.name === 'index') this.state.index = event.target.value;
      if (/^(od|os)-(sph|cyl|axis)$/.test(event.target.name) || event.target.name === 'pd') this.state.rx[event.target.name] = event.target.value;
      if (event.target.name === 'profile-id') {
        const profile = this.profiles.find((item) => item.id === event.target.value);
        if (profile) this.applyProfile(profile);
        else { this.state.profileId = ''; this.state.rx = {}; }
        this.render();
        return;
      }
      if (event.target.name === 'profile-save') {
        this.state.saveProfile = event.target.checked;
        const name = this.content.querySelector('[data-profile-name]');
        if (name) name.hidden = !event.target.checked;
      }
      if (event.target.name === 'profile-name') this.state.profileName = event.target.value;
      if (/^(od|os)-cyl$/.test(event.target.name)) { const axis=this.content.querySelector(`[name="${event.target.name.slice(0,2)}-axis"]`);axis.disabled=!event.target.value;if(!event.target.value){axis.value='';this.state.rx[axis.name]='';} }
      this.updateFooter();
    }

    async advance() {
      this.error.textContent='';
      if(this.state.step===1){this.trackStepComplete();if(this.state.usage==='non-prescription'){this.state.coating='blue';this.state.index='';this.state.step=5;}else{this.state.step=2;}this.render();return;}
      if(this.state.step===2){const message=this.validateRx();if(message){const prompt=this.content.querySelector('[data-rx-prompt]');prompt.textContent='Complete your prescription, or select “Send prescription later” to continue.';prompt.hidden=false;this.content.querySelector('[name="rx-later"]')?.focus();this.publishTracking('lens_step_error',{error_code:'prescription_validation'});return;}if(!this.isRxDeferred()&&this.state.saveProfile){this.next.disabled=true;this.next.textContent='Saving…';try{await this.saveCurrentProfile();}catch(error){this.profilesAvailable=false;this.state.saveProfile=false;this.error.textContent=error.message||'Saving is temporarily unavailable. Continue without saving this prescription.';this.publishTracking('lens_step_error',{error_code:'profile_save_failed'});this.updateFooter();return;}}this.trackStepComplete();this.state.step=3;this.render();return;}
      if(this.state.step===3){this.trackStepComplete();this.state.step=this.state.usage==='non-prescription'?5:4;this.render();return;}
      if(this.state.step===4){this.trackStepComplete();this.state.step=5;this.render();return;}
      this.addToCart();
    }

    previous(){this.publishTracking('lens_step_back');if(this.state.step===5)this.state.step=this.state.usage==='non-prescription'?1:4;else if(this.state.step===4)this.state.step=3;else if(this.state.step===3)this.state.step=this.state.usage==='non-prescription'?1:2;else this.state.step=1;this.lastTrackedStep=0;this.render();}

    updateFooter(){
      let enabled=false,label='';
      if(this.state.step===1){enabled=!!this.state.usage;label=enabled?(this.state.usage==='non-prescription'?'Review order':'Continue to prescription'):'Choose a usage';}
      if(this.state.step===2){enabled=true;label='Continue';}
      if(this.state.step===3){enabled=!!this.state.coating;label=enabled?(this.state.usage==='non-prescription'?'Review order':'Continue to lens index'):'Choose a lens type';}
      if(this.state.step===4){enabled=!!this.state.index;label=enabled?'Review order':'Choose a lens index';}
      if(this.state.step===5){enabled=!!this.state.variant;label='Add to cart';}
      this.next.disabled=!enabled;this.next.textContent=label;
      const candidate=this.state.step===5?this.state.variant:this.lowestCandidate();
      this.total.textContent=candidate?this.money(candidate.price):'Unavailable';
    }

    syncColor(){ this.state.variant=null; }
    selectedColor(){const picker=this.section?.querySelector('variant-selects');const checked=[...picker?.querySelectorAll('input:checked')||[]].find(input=>input.name.toLowerCase().includes('color'));const select=[...picker?.querySelectorAll('select')||[]].find(item=>item.name.toLowerCase().includes('color'));return checked?.value||select?.value||this.variants[0]?.selected.color||'';}
    lensValue(variant){return variant.selected.lens||variant.selected.lense||'';}
    coatingValue(variant){return variant.selected['lens coating']||'';}
    isPrescription(variant){return this.lensValue(variant).includes('Single Vision');}
    isBlue(variant){return /blue[- ]light/i.test(`${this.lensValue(variant)} ${this.coatingValue(variant)}`);}
    isClear(variant){return /clear/i.test(`${this.lensValue(variant)} ${this.coatingValue(variant)}`);}
    availableVariants(){const color=this.selectedColor();return this.variants.filter(variant=>variant.available&&(!variant.selected.color||variant.selected.color===color));}
    candidates({usage='',coating='',index=''}={}){return this.availableVariants().filter(variant=>{
      const lens=this.lensValue(variant);
      if(usage==='non-prescription'&&this.isPrescription(variant))return false;
      if(usage&&usage!=='non-prescription'&&!this.isPrescription(variant))return false;
      if(coating==='blue'&&!this.isBlue(variant))return false;
      if(coating==='clear'&&!this.isClear(variant))return false;
      if(index&&!lens.includes(index))return false;
      return true;
    });}
    resolveVariant(){return this.candidates({usage:this.state.usage,coating:this.state.coating,index:this.state.index})[0]||null;}
    lowestCandidate(){return this.candidates({usage:this.state.usage,coating:this.state.coating,index:this.state.index}).sort((a,b)=>a.price-b.price)[0]||this.availableVariants().sort((a,b)=>a.price-b.price)[0]||null;}
    coatingBaseline(){
      const baseline = this.state.usage === 'non-prescription'
        ? this.candidates({usage:'non-prescription'})
        : this.candidates({usage:this.state.usage, coating:'clear', index:'1.61'});
      return baseline.length ? this.minPrice(baseline) : this.minPrice(this.candidates({usage:this.state.usage}));
    }
    indexBaseline(){
      const baseline = this.candidates({usage:this.state.usage, coating:this.state.coating, index:'1.61'});
      return baseline.length ? this.minPrice(baseline) : this.minPrice(this.candidates({usage:this.state.usage, coating:this.state.coating}));
    }
    minPrice(variants){return Math.min(...variants.map(variant=>variant.price));}
    incrementLabel(price, baseline){const delta=price-baseline;return delta>0?`+${this.money(delta)}`:'Included';}
    includedCoatingMessage(){return '<div class="ls-feature-highlight"><span aria-hidden="true">✦</span><span><strong>Premium coatings included at no extra cost</strong><small>Every Le Seven lens comes with anti-reflective, scratch-resistant, and UV-protective coatings in the base price.</small></span></div>';}
    validateRx(){if(this.isRxDeferred())return '';if(!this.state.rx['od-sph']||!this.state.rx['os-sph']||!this.state.rx.pd)return'Choose SPH for both eyes and PD.';for(const eye of['od','os'])if(!!this.state.rx[`${eye}-cyl`]!==!!this.state.rx[`${eye}-axis`])return'Choose AXIS whenever CYL is selected.';return'';}
    restoreRx(){Object.entries(this.state.rx).forEach(([name,value])=>{const field=this.content.querySelector(`[name="${name}"]`);if(!field)return;if(field.type==='checkbox')field.checked=!!value;else field.value=value;});for(const eye of['od','os']){const cyl=this.content.querySelector(`[name="${eye}-cyl"]`);const axis=this.content.querySelector(`[name="${eye}-axis"]`);axis.disabled=!cyl.value;}}
    numberOptions(min,max,step,placeholder){const options=[];let placeholderAdded=false;for(let n=min;n<=max+.001;n+=step){if(!placeholderAdded&&n>=0){options.push(`<option value="" selected>${placeholder}</option>`);placeholderAdded=true;}const value=n.toFixed(2);options.push(`<option value="${value}">${n>0?'+':''}${value}</option>`);}if(!placeholderAdded)options.splice(Math.floor(options.length/2),0,`<option value="" selected>${placeholder}</option>`);return options.join('');}
    money(cents){return new Intl.NumberFormat(document.documentElement.lang||'en',{style:'currency',currency:this.dataset.currency||'CAD'}).format(cents/100);}
    escape(value){const node=document.createElement('div');node.textContent=value||'';return node.innerHTML;}

    addToCart(){
      const variant=this.resolveVariant();if(!variant||!this.nativeForm){this.error.textContent='This lens combination is unavailable.';return;}
      this.state.variant=variant;
      this.trackStepComplete();
      this.publishTracking('lens_funnel_complete');
      this.funnelCompleted=true;
      this.nativeForm.querySelector('.product-variant-id').value=variant.id;
      this.nativeForm.querySelectorAll('[data-ls-property]').forEach(input=>input.remove());
      const properties={'Lens usage':this.labels[this.state.usage],'Lens type':this.state.coating==='blue'?'Le Seven Signature Blue Light Coating':'Clear lenses','Lens index':this.state.index||'Standard non-prescription'};
      if(this.isRxDeferred())Object.assign(properties,{'Prescription submission':'Email after checkout','Prescription status':'Awaiting prescription','Send prescription to':'contact@lesevenoptical.com'});
      else if(this.state.usage!=='non-prescription')Object.assign(properties,{'Prescription profile':this.state.profileId?(this.state.profileName||'Saved prescription'):'Entered for this order','OD SPH':this.state.rx['od-sph'],'OD CYL':this.state.rx['od-cyl']||'N/A','OD AXIS':this.state.rx['od-axis']||'N/A','OS SPH':this.state.rx['os-sph'],'OS CYL':this.state.rx['os-cyl']||'N/A','OS AXIS':this.state.rx['os-axis']||'N/A','PD':`${this.state.rx.pd} mm`});
      Object.entries(properties).forEach(([name,value])=>{const input=document.createElement('input');input.type='hidden';input.name=`properties[${name}]`;input.value=value;input.dataset.lsProperty='';this.nativeForm.append(input);});
      this.closeReason='add_to_cart';
      this.dialog.close();
      this.nativeForm.requestSubmit(this.nativeForm.querySelector('[name="add"]'));
    }
  });
}
